/**
 * Variant C run-state machine — typed reducer over the run lifecycle
 * states the Loom shell renders against.
 *
 * State machine:
 *
 *      ┌─────────────────────────── reset ──────────────────────────┐
 *      ▼                                                             │
 *    idle ──start──▶ mid-run ──pause-req──▶ pausing                  │
 *                       ▲  ◀──────────────── paused ──resume──┐      │
 *                       │                                      │      │
 *                       │  ◀──resume──────────────────────────┘      │
 *                       │                                             │
 *                       ▲  ◀─── needs_approval ──── approval          │
 *                       │                              ▲              │
 *                       │                              │              │
 *                       │                        mid-run (implicit)   │
 *                       │                                             │
 *                    complete / failed / cancelled                    │
 *                       │                                             │
 *                       ▼                                             │
 *                   post-run ──restart──▶ idle ───────────────────────┘
 *
 * Why a typed reducer instead of XState:
 *   - Six states, eight events. XState's state-charts shine when
 *     hierarchy/parallel regions are needed. Here they aren't.
 *   - Zero new dependencies — keeps the add-in's bundle small.
 *   - Easier to test (a pure function), easier for the team to read.
 *   - If we later need invocations / actor model, swapping in
 *     XState is mechanical: every transition here maps 1:1 to a
 *     state node + transition declaration.
 *
 * SRP: this module owns "what state is the shell in, given the
 * sequence of events". It does NOT:
 *   - know how to fetch backend events (that's the SSE adapter)
 *   - render any UI (that's components)
 *   - persist state (that's the host's job if needed)
 *
 * The reducer is pure: same (state, event) → same next state.
 * Side-effecty bits (start the run, post the approval) live in the
 * components that dispatch the corresponding events.
 */

// ---------------------------------------------------------------------------
// State shape
// ---------------------------------------------------------------------------

import {
  type AppendInput,
  type EventKind,
  type EventLog,
  type EventLogEntry,
  appendEvent,
  emptyEventLog,
} from "./eventLog";

export type RunState = "idle" | "mid-run" | "pausing" | "approval" | "paused" | "post-run";

/**
 * True when the run is actively executing (mid-run or mid-pause
 * hunt). Use instead of ``state === "mid-run"`` wherever "pausing"
 * must be treated identically — e.g. allowing SECTION_ACTIVATED,
 * gating the spine's live-pulse, showing the elapsed-time counter.
 */
export function isActiveRun(s: RunState): boolean {
  return s === "mid-run" || s === "pausing";
}

/**
 * Which top-level surface the shell renders during a run.
 *
 *   "spine"    → structural view, one node per section, status + badges.
 *   "timeline" → chronological view, newest-first event stream.
 *   "drafts"   → cross-run revision history (DraftsPanel). Reachable
 *                from every shell state via the TabBar; not gated on
 *                having an active run.
 *
 * Idle picker + plan review surfaces ignore this — they render their
 * own content regardless of viewMode. viewMode only matters once the
 * writer is looking at the run itself.
 *
 * See ``docs/design/variant-c/spine-vs-timeline.md`` for the two
 * views' contract. Default is "spine" — the run's primary surface.
 */
export type ViewMode = "spine" | "timeline" | "issues" | "drafts" | "images";

/**
 * Composable timeline filter — each dimension narrows independently.
 * Matching an entry means ALL non-null dimensions agree; null means
 * "don't filter this dimension". See ``entryMatchesFilter`` for the
 * match predicate.
 *
 *   sectionId      narrow to one section's thread (e.g. "§9.2" or "CON-401")
 *   kind           narrow to one EventKind (web / regen / ...)
 *   actor          narrow to asthra-originated OR writer-originated
 *   sinceMs        show only entries whose ts >= sinceMs
 *                  (/since break computes this from the log's gap
 *                  structure; see ``computeSinceBreak``)
 *
 * Empty filter (all nulls) matches every entry — the default.
 */
export interface TimelineFilter {
  sectionId: string | null;
  kind: EventKind | null;
  actor: "asthra" | "you" | null;
  sinceMs: number | null;
}

export function emptyTimelineFilter(): TimelineFilter {
  return { sectionId: null, kind: null, actor: null, sinceMs: null };
}

/** True when every non-null dimension of ``filter`` matches ``entry``. */
export function entryMatchesFilter(entry: EventLogEntry, filter: TimelineFilter): boolean {
  if (filter.sectionId && entry.sectionId !== filter.sectionId) return false;
  if (filter.kind && entry.kind !== filter.kind) return false;
  if (filter.actor && entry.actor !== filter.actor) return false;
  if (filter.sinceMs !== null && entry.ts < filter.sinceMs) return false;
  return true;
}

/**
 * Compute the timestamp that ``/since break`` refers to: the ``ts``
 * just after the most recent gap of ≥ ``gapThresholdMs`` between
 * consecutive entries. If no such gap exists (or fewer than 2 entries),
 * returns 0 — effectively "no-op filter, show everything".
 *
 * Default threshold is 45 minutes, matching the prototype's
 * ``/since break`` semantics from spine-vs-timeline.md.
 *
 * Pure function. Iterates entries in insertion order (log.entries),
 * not reversed; linear scan is fine for the session scales we expect
 * (hundreds to low-thousands of events).
 */
export function computeSinceBreak(entries: readonly EventLogEntry[], gapThresholdMs = 45 * 60 * 1000): number {
  if (entries.length < 2) return 0;
  let lastBreakTs = 0;
  for (let i = 1; i < entries.length; i++) {
    const gap = entries[i].ts - entries[i - 1].ts;
    if (gap >= gapThresholdMs) {
      lastBreakTs = entries[i].ts;
    }
  }
  return lastBreakTs;
}

/**
 * A pending approval surfaces in the spine as an inline popout.
 * Carried in state so the UI re-renders with the right context when
 * the writer navigates away and back.
 */
export interface PendingApproval {
  sectionId: string;
  /** Section number string for display (e.g. "9.3"). */
  sectionNumber: string;
  /** Severity tier — drives popout colour and copy. */
  tier: "info" | "block" | "high-stakes";
  /** Question prose surfaced to the writer. */
  question: string;
  /** Excerpt from the source that prompted the question. */
  contextExcerpt: string;
}

/**
 * Active-section pointer for the mid-run state. The spine highlights
 * this node and the inspector defaults to opening it.
 */
export interface ActiveSection {
  sectionId: string;
  sectionNumber: string;
  title: string;
}

export interface VariantCState {
  state: RunState;

  /** Run identifier from the backend; null until a run starts. */
  runId: string | null;

  /** What the agent is working on right now. Null in idle/post-run. */
  activeSection: ActiveSection | null;

  /** Set in approval state; null otherwise. */
  pendingApproval: PendingApproval | null;

  /** Set in post-run with the run's final summary. */
  runSummary: RunSummary | null;

  /** Last fatal error surfaced to the user. Cleared on next valid event. */
  error: RunError | null;

  /** Which top-level view the shell is rendering. Writer toggles via
   *  SpineTimelineToggle. Reset to "spine" on RUN_STARTED and
   *  RESET_TO_IDLE — a new run starts with the structural view. */
  viewMode: ViewMode;

  /** Composable filter applied to the timeline view. Reset on
   *  RUN_STARTED and RESET_TO_IDLE. Each ``/filter …`` command sets
   *  one dimension, leaving others untouched so writers can stack
   *  filters (e.g. ``/filter §9.2`` then ``/filter regen`` =
   *  regens within §9.2). ``/filter clear`` resets all dimensions. */
  timelineFilter: TimelineFilter;

  /** Spec 17 P6.1 — /replay step-through cursor.
   *
   *  null          → not in replay mode; timeline renders normally.
   *  0-based index → cursor points at one entry in the FILTERED,
   *                  newest-first timelineEntries array rendered by
   *                  the shell. TimelineView highlights that row.
   *
   *  Advances by 1 on each /replay. Wraps / stops at the end (the
   *  P6.1 handler enforces; reducer just holds the number).
   *
   *  Reset to null on RUN_STARTED, RESET_TO_IDLE, and whenever the
   *  filter changes (filtering shifts what "index N" means, so the
   *  cursor's position would drift meaninglessly). */
  replayCursor: number | null;

  /** Chronological log of every event that happened in this run.
   *  Shared hub for Spine cumulative badges (P3), Timeline view (P4),
   *  command filters (P5), replay/export/UI-state undo (P6).
   *
   *  Cleared on RUN_STARTED (new run) and RESET_TO_IDLE. Persists
   *  through RUN_FAILED and post-run so the writer can inspect what
   *  happened. See eventLog.ts for the shape + invariants. */
  eventLog: EventLog;

  /** Why the run is paused, when the server said so (set by RUN_PAUSED with a
   *  notice, cleared on resume). Undefined/null for a writer-requested pause. */
  pauseNotice?: string | null;

  /** Module ids the current run was started for (the writer's picked
   *  sections), keyed by run id so a different run loaded later never
   *  inherits it. Null/undefined = whole document or unknown (a restored /
   *  loaded run) — the spine then shows every template section. Set by
   *  RUN_STARTED, widened by RUN_CONTINUED, cleared by RESET_TO_IDLE. */
  runModuleScope?: RunModuleScope | null;
}

export interface RunModuleScope {
  runId: string;
  moduleIds: readonly string[];
}

export interface RunSummary {
  totalSections: number;
  draftedSections: number;
  flaggedSections: number;
  durationMs: number;
  /** The run finished while this pane watched it (live stream end, or the
   *  writer's Stop). Absent on a run restored when the document reopens: the
   *  rail's "Done. N/M sections drafted." is the moment of finishing, not a
   *  standing label, so a restored run doesn't repeat it. */
  live?: boolean;
}

export interface RunError {
  code: string;
  message: string;
  recoverable: boolean;
}

// ---------------------------------------------------------------------------
// Events — the single dispatch vocabulary
// ---------------------------------------------------------------------------

/**
 * Every transition is a discriminated union member. The reducer
 * pattern-matches on ``type`` to decide the next state — exhaustive
 * via TypeScript's never-check at the bottom of the switch.
 */
export type VariantCEvent =
  /** Writer started a draft from the idle screen. */
  | { type: "RUN_STARTED"; runId: string; moduleIds?: readonly string[] | null }
  /** Writer continued omitted sections in an existing run_id. */
  | { type: "RUN_CONTINUED"; runId: string; moduleIds?: readonly string[] | null }
  /** Writer resumed a paused run without resetting its timeline. */
  | { type: "RUN_RESUMED"; runId: string }
  /** Bootstrap rehydration: restore an existing run on app load.
   *  Distinct from RUN_STARTED (which clears eventLog via initialState)
   *  and from RUN_RESUMED (which only accepts "paused"). Lets the
   *  hydration effect drain pendingHydration and then dispatch
   *  RUN_COMPLETED / RESET_TO_IDLE based on the fetched RunStatus. */
  | { type: "RUN_RESTORED"; runId: string }
  /** A restored / loaded run's section scope, read from the run snapshot's
   *  spine rows (the server returns only the run's scoped modules). */
  | { type: "RUN_SCOPE_SET"; runId: string; moduleIds: readonly string[] }
  /** Backend SSE: section.start — agent began drafting a section. */
  | { type: "SECTION_ACTIVATED"; section: ActiveSection }
  /** Backend SSE: section.needs_approval — agent paused for human input. */
  | { type: "APPROVAL_REQUESTED"; approval: PendingApproval }
  /** Writer submitted an approval; backend acked. Resumes the run. */
  | { type: "APPROVAL_RESOLVED" }
  /** Writer clicked Pause — run is hunting for a safe checkpoint.
   *  Transitions mid-run → pausing immediately (before the backend
   *  confirms). Reverted by PAUSE_REVERTED if the API call fails. */
  | { type: "PAUSE_REQUESTED" }
  /** pauseRun() API call failed — roll back pausing → mid-run. */
  | { type: "PAUSE_REVERTED" }
  /** Backend SSE: run paused at a safe checkpoint. ``notice`` carries a
   *  server-given reason worth showing (e.g. the model provider account ran
   *  out of credits); absent for an ordinary writer-requested pause. */
  | { type: "RUN_PAUSED"; notice?: string }
  /** Backend SSE: run.complete. */
  | { type: "RUN_COMPLETED"; summary: RunSummary }
  /** Backend SSE: run.error OR run.cancelled. */
  | { type: "RUN_FAILED"; error: RunError }
  /** Writer started a new run from post-run, or aborted from anywhere. */
  | { type: "RESET_TO_IDLE" }
  /** Append a new entry to the EventLog. Fired by the SSE adapter
   *  for backend-originated events and by UI handlers for
   *  writer-originated ones (prompts, approvals, filter changes). */
  | { type: "EVENT_APPENDED"; entry: AppendInput }
  /** Writer toggled the top-level view between spine + timeline.
   *  Dispatched by SpineTimelineToggle. */
  | { type: "VIEW_MODE_SET"; mode: ViewMode };

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

export const initialState: VariantCState = {
  state: "idle",
  runId: null,
  activeSection: null,
  pendingApproval: null,
  runSummary: null,
  error: null,
  viewMode: "spine",
  timelineFilter: emptyTimelineFilter(),
  replayCursor: null,
  eventLog: emptyEventLog(),
};

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

/**
 * Pure reducer over (current state, event) → next state.
 *
 * Invalid transitions (e.g. ``RUN_COMPLETED`` while in ``idle``) are
 * intentionally no-ops — the reducer logs a console.warn so we
 * notice misordered events in dev, but never throws. Throwing would
 * crash the whole pane on a single misordered backend event, which
 * is a bad tradeoff for a writer-facing tool.
 */
export function variantCReducer(state: VariantCState, event: VariantCEvent): VariantCState {
  switch (event.type) {
    case "RUN_STARTED":
      // Allowed from idle (typical) or post-run (re-run shortcut).
      // Allowing it from approval/mid-run too lets a CLI/test harness
      // jump straight in — the previous run's state is overwritten.
      return {
        ...initialState,
        state: "mid-run",
        runId: event.runId,
        runModuleScope:
          event.moduleIds && event.moduleIds.length > 0
            ? { runId: event.runId, moduleIds: [...event.moduleIds] }
            : null,
        // Preserve session-level events (runId === null: greeting,
        // doctype_selected, library_selected, agent_note suggestions)
        // across the run boundary. The run's own events get cleared
        // (each run is a fresh slate for spine + counters), but the
        // chat audit trail of pre-run conversational steps stays
        // visible. Without this, every run-start wipes everything
        // the writer already saw — only the greeting re-emits via
        // its useEffect, leaving the rest as silent loss.
        eventLog: keepSessionLevelEvents(state.eventLog),
      };

    case "RUN_CONTINUED":
      // Same run identity, new drafting segment. Keep existing event log
      // and run context; just move lifecycle back to mid-run.
      return {
        ...state,
        state: "mid-run",
        runId: event.runId,
        pendingApproval: null,
        runSummary: null,
        error: null,
        // A continuation adds sections to a scoped run: widen its scope. A run
        // with no known scope (whole document / restored) stays unscoped.
        runModuleScope:
          state.runModuleScope?.runId === event.runId && event.moduleIds
            ? {
                runId: event.runId,
                moduleIds: [...new Set([...state.runModuleScope.moduleIds, ...event.moduleIds])],
              }
            : null,
      };

    case "RUN_RESUMED":
      // Valid only from "paused" — the writer explicitly resumed a
      // safe-checkpoint pause. Not from "idle" (that's RUN_STARTED).
      if (state.state !== "paused") {
        return logUnexpected(state, event);
      }
      return {
        ...state,
        state: "mid-run",
        runId: event.runId,
        pendingApproval: null,
        runSummary: null,
        error: null,
        pauseNotice: null,
      };

    case "RUN_SCOPE_SET":
      // A restored run has no RUN_STARTED, so the spine fell back to the
      // whole template ("Drafting · 96 sections" for a 4-section run, Guru
      // 2026-10-14). The snapshot's spine rows are the run's scope.
      return {
        ...state,
        runModuleScope: event.moduleIds.length > 0 ? { runId: event.runId, moduleIds: [...event.moduleIds] } : null,
      };

    case "RUN_RESTORED":
      // Bootstrap rehydration of a previously-drafted document. Valid
      // only from "idle" — entering "mid-run" without going through
      // initialState preserves the bootstrap-restored event log and
      // lets the hydration effect drain pendingHydration before
      // transitioning to RUN_COMPLETED / RESET_TO_IDLE based on the
      // fetched RunStatus.
      if (state.state !== "idle") {
        return logUnexpected(state, event);
      }
      return {
        ...state,
        state: "mid-run",
        runId: event.runId,
        pendingApproval: null,
        runSummary: null,
        error: null,
      };

    case "SECTION_ACTIVATED":
      // The active-section pointer updates whenever the agent
      // switches sections — this happens many times during mid-run.
      // Also valid during "pausing" — the agent is still running to
      // a safe checkpoint and can activate sections in that window.
      // Also valid in post-run when chat-triggered regeneration starts
      // and the shell wants spine to react before regen SSE arrives.
      if (
        state.state !== "mid-run" &&
        state.state !== "approval" &&
        state.state !== "pausing" &&
        state.state !== "post-run"
      ) {
        return logUnexpected(state, event);
      }
      return { ...state, activeSection: event.section, error: null };

    case "APPROVAL_REQUESTED":
      if (state.state !== "mid-run") {
        return logUnexpected(state, event);
      }
      return {
        ...state,
        state: "approval",
        pendingApproval: event.approval,
        error: null,
      };

    case "APPROVAL_RESOLVED":
      if (state.state !== "approval") {
        return logUnexpected(state, event);
      }
      return {
        ...state,
        state: "mid-run",
        pendingApproval: null,
        error: null,
      };

    case "PAUSE_REQUESTED":
      // Dispatch immediately on button click — before the API call.
      // Provides instant UI feedback ("Pausing…") without waiting for
      // the backend to confirm the safe checkpoint.
      if (state.state !== "mid-run") {
        return logUnexpected(state, event);
      }
      return { ...state, state: "pausing" };

    case "PAUSE_REVERTED":
      // pauseRun() API call failed — roll back to mid-run.
      if (state.state !== "pausing") {
        return logUnexpected(state, event);
      }
      return { ...state, state: "mid-run" };

    case "RUN_PAUSED":
      // Accept from mid-run (race: SSE arrives before button click
      // finishes) AND pausing (the expected path after PAUSE_REQUESTED).
      if (state.state !== "mid-run" && state.state !== "pausing" && state.state !== "approval") {
        return logUnexpected(state, event);
      }
      return {
        ...state,
        state: "paused",
        activeSection: null,
        pendingApproval: null,
        error: null,
        pauseNotice: event.notice ?? null,
      };

    case "RUN_COMPLETED":
      if (state.state !== "mid-run" && state.state !== "approval" && state.state !== "paused") {
        return logUnexpected(state, event);
      }
      return {
        ...state,
        state: "post-run",
        runSummary: event.summary,
        pendingApproval: null,
        error: null,
      };

    case "RUN_FAILED":
      // Errors can happen from any state; we record the error but
      // leave the run state alone so the writer can see what they
      // had + retry. RESET_TO_IDLE clears.
      return { ...state, error: event.error };

    case "RESET_TO_IDLE":
      // Same session-level preservation as RUN_STARTED — see comment
      // there. Stop / reset wipes the run's events but keeps the chat
      // audit trail of pre-run steps so the writer doesn't lose
      // everything they saw between greeting and the run.
      return {
        ...initialState,
        eventLog: keepSessionLevelEvents(state.eventLog),
      };

    case "VIEW_MODE_SET": {
      // Idempotent: setting the same mode is a no-op (returns same
      // object) so React skips re-render.
      if (state.viewMode === event.mode) return state;
      return { ...state, viewMode: event.mode };
    }

    case "EVENT_APPENDED": {
      // EVENT_APPENDED is legal from any state. Event-log append is
      // observational — it records what happened without affecting
      // the run lifecycle. appendEvent drops unknown kinds silently
      // (warn + no-op), so a malformed backend event still can't
      // crash the reducer. See eventLog.ts for the full invariant.
      const nextLog = appendEvent(state.eventLog, event.entry);
      if (nextLog === state.eventLog) return state;
      return { ...state, eventLog: nextLog };
    }

    default:
      return assertUnreachable(state, event);
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Build a fresh EventLog containing ONLY entries with ``runId === null``
 * — the session-level chat audit trail (greeting, doctype/library
 * selections, agent_note suggestions). Run-scoped entries (drafts,
 * regens, web searches, approvals — anything stamped with a runId)
 * are dropped.
 *
 * Used by the run-boundary reducer cases (RUN_STARTED, RESET_TO_IDLE)
 * so each new run starts with fresh per-section counters + a fresh
 * timeline view, while the writer's pre-run conversational history
 * persists. Without this, every stop / start wipes everything except
 * the greeting (which re-emits via its own useEffect), creating
 * silent loss of the doctype/library picks + suggestion exchanges.
 *
 * Pure: rebuilds via ``appendEvent`` so counters + ``bySection`` are
 * recomputed from the filtered set rather than carried over with
 * stale references to the dropped entries.
 */
function keepSessionLevelEvents(prev: VariantCState["eventLog"]) {
  const out = emptyEventLog();
  if (prev.entries.length === 0) return out;
  let acc = out;
  for (const entry of prev.entries) {
    if (entry.runId !== null) continue;
    acc = appendEvent(acc, {
      ts: entry.ts,
      actor: entry.actor,
      sectionId: entry.sectionId,
      sectionLabel: entry.sectionLabel,
      runId: entry.runId,
      payload: entry.payload,
    });
  }
  return acc;
}

function logUnexpected(state: VariantCState, event: VariantCEvent): VariantCState {
  // Dev signal only — no throw, no toast. A misordered event is
  // almost always a backend race; we want it visible without
  // breaking the writer's flow.
  //
  // Stryker disable next-line all: observability-only. Mutating
  // the log text, the console guard, or the log payload changes
  // DEV TELEMETRY only, not runtime behaviour. Tests assert the
  // reducer's state transitions (which we cover); they don't pin
  // log format.
  if (typeof console !== "undefined") {
    console.warn("[variantCReducer] unexpected event for state", {
      state: state.state,
      event,
    });
  }
  return state;
}

/**
 * Compile-time exhaustiveness check. If a new event type is added to
 * VariantCEvent without a case here, TypeScript fails this assignment
 * — the loud "never not assignable" error is the alarm.
 */
function assertUnreachable(state: VariantCState, event: never): VariantCState {
  // Stryker disable next-line all: observability-only. See logUnexpected.
  if (typeof console !== "undefined") {
    console.warn("[variantCReducer] unhandled event", event);
  }
  return state;
}
